import { useTranslation } from 'react-i18next';
import type { SyncStatus } from '@jisy/shared';
import styles from './SyncProgress.module.css';

/**
 * Ход синхронизации.
 *
 * Два вида на выбор: карточка по центру, когда показывать всё равно нечего, и полоса над
 * таблицей, когда данные уже есть. Второй нужен для ручного обновления: прятать за индикатором
 * заполненную таблицу незачем, но и молчать о том, что работа идёт, нельзя.
 */
export function SyncProgress({ status }: { status: SyncStatus }): React.ReactElement {
  const { t } = useTranslation();

  // Пока разделы не пересчитаны, доля неизвестна — полоса бежит, а не замирает на середине.
  const known = status.projectsTotal > 0;
  const percent = known
    ? Math.min(100, Math.round((status.projectsDone / status.projectsTotal) * 100))
    : 0;

  return (
    <div className={styles.wrap} role="status" aria-live="polite">
      <div className={styles.card}>
        <span className={styles.title}>{t('sync.title')}</span>
        <p className={styles.stage}>{t(`sync.stage.${status.stage ?? 'queued'}`)}</p>

        <div className={styles.track}>
          <div
            className={known ? styles.fill : styles.fillIndeterminate}
            style={known ? { width: `${percent}%` } : undefined}
          />
        </div>

        <div className={styles.counters}>
          {known && (
            <span className="mono">
              {t('sync.sections', { done: status.projectsDone, total: status.projectsTotal })}
            </span>
          )}
          {status.issuesFetched > 0 && (
            <span className="mono">{t('sync.issues', { count: status.issuesFetched })}</span>
          )}
        </div>

        <p className={styles.hint}>{t('sync.hint')}</p>
      </div>
    </div>
  );
}

/**
 * Полоса над таблицей: то же самое, что и карточка, но не занимает места. Показывается,
 * когда в таблице уже есть строки — например, после кнопки «Обновить».
 */
export function SyncBar({ status }: { status: SyncStatus }): React.ReactElement {
  const { t } = useTranslation();

  const known = status.projectsTotal > 0;
  const percent = known
    ? Math.min(100, Math.round((status.projectsDone / status.projectsTotal) * 100))
    : 0;

  return (
    <div className={styles.bar} role="status" aria-live="polite">
      <div className={styles.barTrack}>
        <div
          className={known ? styles.fill : styles.fillIndeterminate}
          style={known ? { width: `${percent}%` } : undefined}
        />
      </div>

      <div className={styles.barText}>
        <span>{t(`sync.stage.${status.stage ?? 'queued'}`)}</span>
        {known && (
          <span className="mono">
            {t('sync.sections', { done: status.projectsDone, total: status.projectsTotal })}
          </span>
        )}
        {status.issuesFetched > 0 && (
          <span className="mono">{t('sync.issues', { count: status.issuesFetched })}</span>
        )}
      </div>
    </div>
  );
}
